<template>
	<div class="d-flex flex-column overflow-auto">
		<DialogBody>
			<div class="d-flex flex-column">
				<div class="d-flex flex-column gap-5">
					<TripName
						v-model="form.name"
						:error="formErrors.name"
						@input="formErrors.name = undefined"
					/>
					<TripDestinationSelector
						v-model="destination"
						:is-x-s="isXS"
						:error="formErrors.destinations"
						@input="formErrors.destinations = undefined"
					/>
					<TripDuration
						:start-date="form.startDate"
						:end-date="form.endDate"
						@update:start-date="form.startDate = $event"
						@update:end-date="form.endDate = $event"
						:is-x-s="isXS"
						:is-mobile="isMobile"
					/>
					<div class="d-flex">
						<PButton
							v-if="trip?.isRemovable"
							type="text"
							color="error"
							icon="trash"
							@click="deleteItinerary"
						>
							{{ $t('web.edit.trip.detele.title') }}
						</PButton>
					</div>
				</div>
			</div>
		</DialogBody>
		<DialogActions
			:disable-accept="loader"
			:accept-action="{ text: $t('common.save_changes') }"
			:cancel-action="{ text: $t('common.cancel') }"
			@accept="save"
			@cancel="$emit('close')"
		/>
	</div>
</template>

<script setup lang="ts">
	import { storeToRefs } from 'pinia'
	import { computed, onMounted, ref } from 'vue'
	import { TripMenuMetric } from '~/actions/metrics/tripMenu'
	import TripActions from '~/actions/trip/action'
	import { useTrip } from '~/composables/trip'
	import { Destination2 } from '~/models/Destination'
	import { Itinerary2 } from '~/models/Itinerary'
	import { MyTripsNavigation } from '~/services/navigation/myTrips'
	import { useDeviceStore } from '~/store/device'

	interface Props {
		trip: Itinerary2
	}

	const deviceStore = useDeviceStore()
	const { isXS, isMobile } = storeToRefs(deviceStore)

	const props = defineProps<Props>()
	const emit = defineEmits(['close', 'accept'])

	const { trip } = useTrip(props.trip.id!)

	const form = ref<Itinerary2>({ ...props.trip })
	const formErrors = ref<Partial<Record<keyof Itinerary2, string>>>({})

	const destination = computed({
		get: (): Destination2 | null => {
			return (
				(form.value.destination as Destination2) ||
				(form.value.destinations?.[0] as Destination2) ||
				null
			)
		},
		set: (value: Destination2) => {
			form.value.destination = value
			form.value.destinations = value ? [value] : []
		},
	})

	const loader = ref(false)

	onMounted(async () => {
		viewTripDialogEvent()
	})

	const editItinerary = async (): Promise<Itinerary2> => {
		const editedTrip = await TripActions.editTrip(form.value as Itinerary2)
		trip.value = editedTrip
		return editedTrip
	}

	const deleteItinerary = async () => {
		const dialog = TripActions.createDeleteItineraryDialog()
		dialog.open()

		dialog.onAccept(() => {
			if (!form.value.id) return
			TripActions.deleteItinerary(form.value.id!)
			MyTripsNavigation.go()
		})
	}

	const save = async () => {
		loader.value = true
		const errors = await TripActions.validateTrip(form.value as Itinerary2)

		if (errors) {
			formErrors.value = errors
			loader.value = false
			return
		}

		await editItinerary()
		emit('accept')
		loader.value = false
	}

	const viewTripDialogEvent = () => {
		const destination = props.trip?.destination
		TripMenuMetric.viewTripDialog(destination)
	}
</script>
